import {
    BaseChart,
    ChartMouseHandler,
    getConfiguredInstance,
    useChart,
} from '@/components/ECharts';
import { useCompetitorsMediaInvestmentContext } from '@/dashboards/CompetitorsMediaInvestment/hooks/useCompetitorsMediaInvestmentContext';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { jsxToHtml } from '@/utils/reactUtils';
import { Typography } from '@analytical-alley/ui';
import EChartsReact from 'echarts-for-react';
import { isArray } from 'lodash';
import React, {
    Ref,
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    AreaTooltipProps,
    AreaTooltip
} from './AreaTooltip';
import {
    ChartOptions
} from '@/dashboards/CompetitorsMediaInvestment/components/CompetitorsMediaSeasonalityGraph/getGraphData';
import {
    getGraphData
} from '@/dashboards/CompetitorsMediaInvestment/components/CompetitorMediaEffectGraph/getGraphData';

interface GraphProps {
    chartRef?: Ref<EChartsReact | null>;
    selected: string;
    onSelected: (value: string) => void;
    getChartOptions: (chartOptionSeries: ChartOptions['series']) => void;
}

export const CompetitorMediaEffectGraph = memo(
    ({ chartRef, selected, onSelected, getChartOptions }: GraphProps) => {
        const {
            competitorsMediaEffectData: { dates, variableData },
            getVisibleContributionData,
            period,
        } = useCompetitorsMediaInvestmentContext();
        const { contributionFormatter } = useDashboardGlobalContext();

        const eChartsOptions = useMemo(() => {
            return getGraphData({
                seriesData: getVisibleContributionData(),
                xAxisData: dates,
                period: period,
                formatNumber: contributionFormatter,
            });
        }, [dates, getVisibleContributionData, contributionFormatter, period]);

        const [hovered, setHovered] = useState('');

        const selectedRef = useRef(selected);
        const timeoutRef = useRef<number | null>(null);

        useEffect(() => {
            getChartOptions(eChartsOptions.series);
        }, [eChartsOptions, getChartOptions]);

        useEffect(() => {
            selectedRef.current = selected;
        }, [selected]);

        const onClick: ChartMouseHandler = useCallback(
            (context) => {
                if (context.seriesName) {
                    onSelected(context.seriesName);
                } else {
                    onSelected('');
                }
            },
            [onSelected],
        );

        const onMouseOver: ChartMouseHandler = useCallback((context) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (context.seriesName) {
                setHovered(
                    selectedRef.current
                        ? selectedRef.current
                        : context.seriesName,
                );
            }
        }, []);

        const onMouseOut: ChartMouseHandler = useCallback(() => {
            if (selectedRef.current) {
                return;
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = window.setTimeout(() => {
                setHovered('');
            }, 0);
        }, []);

        const { ref, chartOptions, onEvents, isChartReady, onChartReady } =
            useChart({
                chartOptions: eChartsOptions,
                onClick,
                onMouseOver,
                onMouseOut,
            });

        useEffect(() => {
            // Guarded: until the option has landed, `BaseChart` renders with
            // `notMerge`, so anything set here would be discarded — and a
            // series-carrying partial option would throw `xAxis "0" not found`.
            const instance = getConfiguredInstance(ref.current);

            if (instance) {
                instance.setOption({
                    tooltip: {
                        showDelay: selected ? 0 : 50,
                        show: true,
                        formatter: (params: AreaTooltipProps) => {
                            return jsxToHtml(
                                <AreaTooltip
                                    params={params}
                                    selectedSeriesName={hovered}
                                    formatNumber={contributionFormatter}
                                />,
                            );
                        },
                    },
                });
            }
        }, [
            ref,
            hovered,
            selected,
            variableData,
            contributionFormatter,
            isChartReady,
        ]);

        useEffect(() => {
            const instance = getConfiguredInstance(ref.current);

            const series =
                chartOptions?.series && isArray(chartOptions.series)
                    ? chartOptions.series
                    : [];

            if (instance) {
                instance.setOption({
                    series: series.map((seriesOption) => {
                        if (selected && selected !== seriesOption.name) {
                            return {
                                ...seriesOption,
                                itemStyle: { opacity: 0.1 },
                                lineStyle: { opacity: 0 },
                                areaStyle: { opacity: 0.1 },
                                symbol: 'none',
                            };
                        }
                        return {
                            ...seriesOption,
                            itemStyle: { opacity: 0.8 },
                            lineStyle: { opacity: 0.6 },
                            symbol: 'circle',
                        };
                    }),
                });
            }
        }, [ref, chartOptions.series, selected, isChartReady]);

        useImperativeHandle(chartRef, () => ref.current, [ref]);

        return (
            <div className="relative w-full h-180">
                <div className="absolute top-88 -left-26">
                    <Typography
                        className="-rotate-90 font-mono"
                        variant="bodyM"
                    >
                        Competitor Media Effect
                    </Typography>
                </div>
                <div className="relative left-10 h-full">
                    <BaseChart
                        $shouldExpand
                        option={chartOptions}
                        ref={ref}
                        onEvents={onEvents}
                        onChartReady={onChartReady}
                    />
                </div>
            </div>
        );
    },
);

CompetitorMediaEffectGraph.displayName = 'CompetitorMediaEffectGraph';
